import { api } from "@/lib/api";

export interface SavedSearch {
  id: number;
  name: string;
  query: string;
  /** JSON array of indexer ids; null = all enabled. */
  indexer_ids_json: string | null;
  /** JSON array of newznab numeric category codes; null = all. */
  categories_json: string | null;
  schedule_cron: string | null;
  max_size_bytes: number | null;
  min_seeders: number | null;
  last_run_at: string | null;
  last_hit_count: number | null;
  enabled: boolean;
  auto_grab: boolean;
  created_at: string;
  updated_at: string;
}

export interface SavedSearchHit {
  id: number;
  saved_search_id: number;
  seen_at: string;
  release_title: string;
  release_guid: string;
  indexer_id: number | null;
  size_bytes: number | null;
  seeders: number | null;
  grabbed: boolean;
  download_client_id: number | null;
  external_id: string | null;
}

export interface SavedSearchDetail {
  saved_search: SavedSearch;
  recent_hits: SavedSearchHit[];
  next_fires: string[];
}

export interface RunOutcome {
  saved_search_id: number;
  total_hits: number;
  fresh_hits: number;
  grab_dispatched: boolean;
  grabbed_release_guid: string | null;
}

export interface CreateSavedSearchRequest {
  name: string;
  query: string;
  /** Empty / missing = all enabled indexers. */
  indexer_ids?: number[];
  categories?: number[];
  /** 5-field cron (`min hour dom mon dow`). Missing = manual-only. */
  schedule_cron?: string | null;
  max_size_bytes?: number | null;
  min_seeders?: number | null;
  enabled?: boolean;
  auto_grab?: boolean;
}

export interface UpdateSavedSearchRequest {
  name?: string;
  query?: string;
  indexer_ids?: number[] | null;
  categories?: number[] | null;
  schedule_cron?: string | null;
  max_size_bytes?: number | null;
  min_seeders?: number | null;
  enabled?: boolean;
  auto_grab?: boolean;
}

export const savedSearchesApi = {
  list: () => api<SavedSearch[]>("/api/v1/saved-searches"),
  get: (id: number) => api<SavedSearchDetail>(`/api/v1/saved-searches/${id}`),
  create: (body: CreateSavedSearchRequest) =>
    api<SavedSearch>("/api/v1/saved-searches", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: number, body: UpdateSavedSearchRequest) =>
    api<SavedSearch>(`/api/v1/saved-searches/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<void>(`/api/v1/saved-searches/${id}`, { method: "DELETE" }),
  run: (id: number) =>
    api<RunOutcome>(`/api/v1/saved-searches/${id}/run`, { method: "POST" }),
  hits: (id: number, limit = 100) =>
    api<SavedSearchHit[]>(
      `/api/v1/saved-searches/${id}/hits?limit=${limit}`,
    ),
};
